import { invokeWithRetry } from "./common/invokeWithRetry";
import type { GymCalendar } from "@/types/types";
import i18n from "@/i18n";

const FUNCTION_NAME = "calendars-management";

export interface CalendarFormData {
  name: string;
  description?: string;
  color?: string;
  is_active?: boolean;
  sort_order?: number;
}

export async function listCalendars(
  gymId: string,
  includeInactive = false
): Promise<GymCalendar[]> {
  const { data, error, authExpired } = await invokeWithRetry(FUNCTION_NAME, {
    action: "list_calendars",
    gym_id: gymId,
    include_inactive: includeInactive,
  });

  if (authExpired) return [];
  if (error) throw new Error(error.message ?? i18n.t("calendars.loadFailed", { ns: "appui" }));

  return data?.calendars ?? [];
}

export async function createCalendar(
  gymId: string,
  formData: CalendarFormData
): Promise<GymCalendar> {
  const { data, error, authExpired } = await invokeWithRetry(FUNCTION_NAME, {
    action: "create_calendar",
    gym_id: gymId,
    data: formData,
  });

  if (authExpired) throw new Error(i18n.t("calendars.sessionExpired", { ns: "appui" }));
  if (error) throw new Error(error.message ?? i18n.t("calendars.createFailed", { ns: "appui" }));

  return data?.calendar;
}

export async function updateCalendar(
  gymId: string,
  calendarId: string,
  formData: Partial<CalendarFormData>
): Promise<GymCalendar> {
  const { data, error, authExpired } = await invokeWithRetry(FUNCTION_NAME, {
    action: "update_calendar",
    gym_id: gymId,
    calendar_id: calendarId,
    data: formData,
  });

  if (authExpired) throw new Error(i18n.t("calendars.sessionExpired", { ns: "appui" }));
  if (error) throw new Error(error.message ?? i18n.t("calendars.updateFailed", { ns: "appui" }));

  return data?.calendar;
}

export async function deleteCalendar(gymId: string, calendarId: string): Promise<void> {
  const { error, authExpired } = await invokeWithRetry(FUNCTION_NAME, {
    action: "delete_calendar",
    gym_id: gymId,
    calendar_id: calendarId,
  });

  if (authExpired) throw new Error(i18n.t("calendars.sessionExpired", { ns: "appui" }));
  if (error) throw new Error(error.message ?? i18n.t("calendars.deleteFailed", { ns: "appui" }));
}

export async function getAccessibleCalendars(
  userId: string,
  gymId: string
): Promise<GymCalendar[]> {
  const { data, error, authExpired } = await invokeWithRetry(FUNCTION_NAME, {
    action: "get_accessible_calendars",
    gym_id: gymId,
    user_id: userId,
  });

  if (authExpired) return [];
  if (error) throw new Error(error.message ?? i18n.t("calendars.loadAccessibleFailed", { ns: "appui" }));

  return data?.calendars ?? [];
}
